import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import Footer from './Footer';
import SEO from './SEO';
import './Signup.css';
import { needsProfileCompletion } from "../utils/profileCompletion";
import { fireCheckoutIntent } from "../utils/checkoutAnalytics";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firmName, setFirmName] = useState('');
  const [role, setRole] = useState('Attorney');
  const [showPassword, setShowPassword] = useState(false);
  const { showWarning, confirm } = useNotification();
  const [agreeToEmailTerms, setAgreeToEmailTerms] = useState(false);
  const [agreeToGoogleTerms, setAgreeToGoogleTerms] = useState(false);
  const [googleTermsHint, setGoogleTermsHint] = useState(false);
  const [emailTermsHint, setEmailTermsHint] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '', firmName: '' });
  const [touched, setTouched] = useState({ email: false, password: false, firmName: false });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleInitialized, setGoogleInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Normalize URL - remove trailing slash to ensure consistency for OAuth origins
  useEffect(() => {
    if (location.pathname !== '/signup' && location.pathname.endsWith('/')) {
      const normalizedPath = location.pathname.replace(/\/+$/, '');
      navigate(normalizedPath + location.search, { replace: true });
    }
  }, [location, navigate]);

  const fromState = location.state?.from;
  const redirectPath = typeof fromState === 'string' ? fromState : fromState?.pathname || '/research';
  const postAuthRedirectStarted = React.useRef(false);
  const { signup, signInWithGoogle, requestSignup, isAuthenticated, loading: authLoading, user } = useAuth();

  // After signup, check if user had selected a plan on Pricing page and redirect to Stripe checkout
  const startPendingCheckout = React.useCallback(async () => {
    const raw = localStorage.getItem("pendingPlan");
    if (!raw) return false;
    try {
      const plan = JSON.parse(raw);
      localStorage.removeItem("pendingPlan");
      const token = localStorage.getItem("token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;
      const event_id = await fireCheckoutIntent(plan, user);
      const res = await axios.post(`${API_URL}/stripe/create-checkout-session`, {
        planName: plan.name.toLowerCase(),
        price: plan.price,
        event_id,
      }, { headers, withCredentials: true });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return true;
      }
    } catch (err) {
      console.error("Failed to start pending checkout:", err);
      localStorage.removeItem("pendingPlan");
    }
    return false;
  }, []);

  const handlePostAuthRedirect = React.useCallback(async () => {
    if (postAuthRedirectStarted.current) {
      return;
    }
    postAuthRedirectStarted.current = true;

    try {
      const token = localStorage.getItem("token");
      const config = { withCredentials: true };
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }

      const checkoutStarted = await startPendingCheckout();
      if (checkoutStarted) {
        return;
      }

      const subscriptionResponse = await axios.get(`${API_URL}/payments/subscription-status`, config);
      const subscription = subscriptionResponse.data;
      const hasActiveSubscription =
        subscription.subscriptionStatus === "active" ||
        subscription.subscriptionStatus === "trialing";
      const hasUsageAllotted =
        (subscription.aiMonthlyLimit || 0) > 0 ||
        (subscription.subscriptionActionsLimit || 0) > 0 ||
        (user?.aiMonthlyLimit || 0) > 0 ||
        (user?.subscriptionActionsLimit || 0) > 0;
      const hasAdminAllocatedActions =
        (subscription.adminAllocatedActionsTotal || 0) > 0 ||
        (user?.adminAllocatedActionsTotal || 0) > 0;
      const isFirstTimeUser =
        !hasActiveSubscription && !hasUsageAllotted && !hasAdminAllocatedActions;

      if (needsProfileCompletion(user)) {
        // Always complete profile (including "Heard About Us") before pricing
        const afterProfilePath = isFirstTimeUser ? "/pricing" : redirectPath;
        navigate("/complete-profile", { replace: true, state: { from: afterProfilePath } });
      } else if (isFirstTimeUser) {
        navigate("/pricing", { replace: true });
      } else {
        navigate(redirectPath, { replace: true });
      }
    } catch (error) {
      console.error("Error checking subscription status after signup:", error);
      navigate(needsProfileCompletion(user) ? "/complete-profile" : redirectPath, { replace: true });
    }
  }, [navigate, redirectPath, startPendingCheckout, user]);

  const googleButtonRef = React.useRef(null);
  const userInitiatedGoogleAction = React.useRef(false);

  // Check for tester/user invitation token in URL
  const [testerToken, setTesterToken] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [invitationEmail, setInvitationEmail] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      setError(''); // Clear any errors when authenticated
      handlePostAuthRedirect();
    }
  }, [isAuthenticated, authLoading, handlePostAuthRedirect]);

  useEffect(() => {
    if (!isAuthenticated) {
      postAuthRedirectStarted.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setError('');
    setFieldErrors({ email: '', password: '', firmName: '' });
    setTouched({ email: false, password: false, firmName: false });

    const urlParams = new URLSearchParams(location.search);
    const testerTokenParam = urlParams.get('testerToken');
    const userTokenParam = urlParams.get('userToken');
    const emailParam = urlParams.get('email');
    const tokenParam = urlParams.get('token');
    const verifiedParam = urlParams.get('verified');

    if (tokenParam && emailParam && verifiedParam === 'true') {
      setVerificationToken(tokenParam);
      setEmailVerified(true);
      setEmail(emailParam);
    } else if (testerTokenParam && emailParam) {
      setTesterToken(testerTokenParam);
      setInvitationEmail(emailParam);
      setEmail(emailParam);
    } else if (userTokenParam && emailParam) {
      setUserToken(userTokenParam);
      setInvitationEmail(emailParam);
      setEmail(emailParam);
    }
  }, [location.search]);

  const handleGoogleCallback = React.useCallback(async (response) => {
    if (!response.credential) {
      setError('Google sign-in failed. Please try again.');
      setGoogleLoading(false);
      userInitiatedGoogleAction.current = false;
      return;
    }

    setGoogleLoading(true);
    setError(''); // Clear any previous errors

    const result = await signInWithGoogle(response.credential, 'signup', role);
    setGoogleLoading(false);
    userInitiatedGoogleAction.current = false; // Reset flag after callback

    if (result.success) {
      setError(''); // Clear errors on success
      await handlePostAuthRedirect();
    } else {
      if (result.message?.includes('Please sign in instead')) {
        const shouldGoToLogin = await confirm({
          title: 'Account Already Exists',
          message: result.message,
          confirmText: 'Go to Sign In',
          cancelText: 'Close',
          type: 'warning',
        });
        if (shouldGoToLogin) {
          navigate('/login');
          return;
        }
      }
      // Show all error messages to the user
      if (result.message) {
        setError(result.message);
      } else {
        setError('Google sign-in failed. Please try again.');
      }
    }
  }, [signInWithGoogle, handlePostAuthRedirect, confirm, navigate]);

  // Handle Google sign-in errors - only for actual user-facing errors
  useEffect(() => {
    const handleGoogleError = (error) => {
      setGoogleLoading(false);

      // Don't show errors for user cancellations
      if (error.type === 'popup_closed_by_user' || error.type === 'user_cancelled') {
        userInitiatedGoogleAction.current = false;
        return;
      }

      // Only show errors if user actually tried to use Google sign-in
      if (!userInitiatedGoogleAction.current) {
        return;
      }

      const errorMessage = error.message || error.detail?.message || error.error || '';

      // Only show critical errors that prevent functionality
      if (errorMessage.includes('popup_blocked')) {
        setError('Google Sign-In Error: Popup was blocked. Please allow popups for this site and try again.');
      } else if (errorMessage.includes('access_denied')) {
        // User denied access, don't show error
        userInitiatedGoogleAction.current = false;
        return;
      } else if (errorMessage && !errorMessage.includes('invalid_client') && !errorMessage.includes('401') && !errorMessage.includes('no registered origin')) {
        // Only show non-configuration errors for user actions
        setError(`Google Sign-In Error: ${errorMessage}`);
      }

      // Reset flag after handling
      userInitiatedGoogleAction.current = false;
    };

    // Listen for Google errors from the library - only handle actual error events
    const errorHandler = (e) => {
      // Only handle if it's a real error event, not console warnings
      if (e.type === 'error' || e.detail) {
        handleGoogleError(e.detail || e);
      }
    };

    window.addEventListener('google-signin-error', errorHandler);

    return () => {
      window.removeEventListener('google-signin-error', errorHandler);
    };
  }, []);

  // Load Google Identity Services script
  useEffect(() => {
    // Cancel any existing One Tap prompts immediately on mount
    const cancelOneTap = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        try {
          window.google.accounts.id.cancel();
        } catch (e) {
          // Ignore errors
        }
      }
    };

    // Cancel immediately
    cancelOneTap();

    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.warn('Google Client ID not configured. Google sign-in will not work.');
      return;
    }

    // Set up interval to keep canceling One Tap (in case it tries to show)
    const cancelInterval = setInterval(() => {
      cancelOneTap();
    }, 500);

    // Check if script is already loaded
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      // Script already loaded, just initialize (but don't auto-prompt)
      if (window.google && window.google.accounts) {
        try {
          const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
          if (!clientId) {
            // Don't show error on initialization - only when user tries to use it
            return;
          }

          cancelOneTap(); // Cancel any existing prompts
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleCallback,
            auto_select: false, // Don't auto-select
            cancel_on_tap_outside: true, // Cancel if user clicks outside
            itp_support: true, // Enabled ITP support for better Incognito/Safari compatibility
          });
          // Explicitly cancel any prompts that might have been triggered
          setTimeout(() => {
            try {
              window.google.accounts.id.cancel();
            } catch (e) { }
          }, 100);

          // Render the hidden Google button
          if (window.google.accounts.id && googleButtonRef.current) {
            try {
              window.google.accounts.id.renderButton(googleButtonRef.current, {
                theme: 'outline',
                size: 'large',
                text: 'signup_with',
                width: 300,
              });
              setGoogleInitialized(true);
              setError(''); // Clear any errors if initialization succeeds
            } catch (error) {
              console.error('Error rendering Google button:', error);
              // Don't show error if it's just a configuration warning and button might still work
              // Only log to console, don't show to user unless they try to use it
            }
          }
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
          // Don't show initialization errors to user - they might be false positives
          // Only show errors when user actually tries to use Google sign-in
        }
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.accounts) {
        try {
          const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
          if (!clientId) {
            // Don't show error on initialization - only when user tries to use it
            return;
          }

          cancelOneTap(); // Cancel any existing prompts
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleCallback,
            auto_select: false, // Don't auto-select
            cancel_on_tap_outside: true, // Cancel if user clicks outside
            itp_support: true, // Enabled ITP support for better Incognito/Safari compatibility
          });
          // Explicitly cancel any prompts that might have been triggered
          setTimeout(() => {
            try {
              window.google.accounts.id.cancel();
            } catch (e) { }
          }, 100);

          // Render the hidden Google button
          if (window.google.accounts.id && googleButtonRef.current) {
            try {
              window.google.accounts.id.renderButton(googleButtonRef.current, {
                theme: 'outline',
                size: 'large',
                text: 'signup_with',
                width: 300,
              });
              setGoogleInitialized(true);
              setError(''); // Clear any errors if initialization succeeds
            } catch (error) {
              console.error('Error rendering Google button:', error);
              // Don't show error if it's just a configuration warning and button might still work
              // Only log to console, don't show to user unless they try to use it
            }
          }
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
          // Don't show initialization errors to user - they might be false positives
          // Only show errors when user actually tries to use Google sign-in
        }
      }
    };
    script.onerror = () => {
      console.error('Failed to load Google Identity Services script');
    };
    document.body.appendChild(script);

    // Cancel One Tap on component mount/unmount
    cancelOneTap();

    return () => {
      // Cancel One Tap when component unmounts and clear interval
      clearInterval(cancelInterval);
      cancelOneTap();
    };
  }, [handleGoogleCallback]);

  if (authLoading) {
    return (
      <div className="signup-page">
        <div className="signup-container">
          <div className="signup-card">
            <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  const validateEmail = (emailValue) => {
    if (!emailValue) {
      return 'Email address is required';
    }
    // Valid email format: allows letters, numbers, dots, hyphens, underscores, plus signs, and @ symbol
    // Rejects special characters like $, %, ^, etc.
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(emailValue)) {
      return 'Please enter a valid email address. Special characters like $, %, ^ are not allowed.';
    }
    return '';
  };

  const validatePassword = (passwordValue) => {
    if (!passwordValue) {
      return 'Password is required';
    }
    // Password must have:
    // - At least 8 characters
    // - At least one uppercase letter
    // - At least one lowercase letter
    // - At least one number
    // - At least one special character (symbol)
    const minLength = passwordValue.length >= 8;
    const hasUpperCase = /[A-Z]/.test(passwordValue);
    const hasLowerCase = /[a-z]/.test(passwordValue);
    const hasNumber = /[0-9]/.test(passwordValue);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(passwordValue);

    if (!minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character (!@#$%^&* etc.)';
    }
    return '';
  };

  const validateFirmName = (nameValue) => {
    if (!nameValue) {
      return 'Firm, company, or your name is required';
    }
    if (nameValue.trim().length === 0) {
      return 'Firm name cannot be empty';
    }
    // Firm name should allow letters, numbers, spaces, hyphens, apostrophes, and periods
    // Reject other special characters
    const firmNameRegex = /^[a-zA-Z0-9\s'.-]+$/;
    if (!firmNameRegex.test(nameValue)) {
      return 'Firm name can only contain letters, numbers, spaces, hyphens, apostrophes, and periods';
    }
    return '';
  };

  // Handle field blur
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'email') {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(email) }));
    } else if (field === 'password') {
      setFieldErrors(prev => ({ ...prev, password: validatePassword(password) }));
    } else if (field === 'firmName') {
      setFieldErrors(prev => ({ ...prev, firmName: validateFirmName(firmName) }));
    }
  };

  // Handle field changes
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(value) }));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) {
      setFieldErrors(prev => ({ ...prev, password: validatePassword(value) }));
    }
  };

  const handleFirmNameChange = (e) => {
    const value = e.target.value;
    setFirmName(value);
    if (touched.firmName) {
      setFieldErrors(prev => ({ ...prev, firmName: validateFirmName(value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    setTouched({ email: true, password: true, firmName: true });

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    const firmNameError = validateFirmName(firmName);

    setFieldErrors({
      email: emailError,
      password: passwordError,
      firmName: firmNameError
    });

    if (emailError || passwordError || firmNameError) {
      return;
    }

    if (!agreeToEmailTerms) {
      setEmailTermsHint(true);
      return;
    }

    setLoading(true);

    if (emailVerified && verificationToken) {
      const result = await signup(firmName, email, password, verificationToken, testerToken, userToken, role);
      setLoading(false);

      if (result.success) {
        await handlePostAuthRedirect();
      } else {
        const backendError = result.message || 'Signup failed. Please try again.';
        setError(backendError);
        if (backendError.toLowerCase().includes('email') || backendError.toLowerCase().includes('already exists')) {
          setFieldErrors(prev => ({ ...prev, email: backendError }));
        }
      }
    } else {
      const result = await requestSignup(firmName, email, password, role);
      setLoading(false);

      if (result.success) {
        setVerificationEmailSent(true);
        setError('');
      } else {
        const backendError = result.message || 'Failed to send verification email. Please try again.';
        setError(backendError);
        if (backendError.toLowerCase().includes('email') || backendError.toLowerCase().includes('already exists')) {
          setFieldErrors(prev => ({ ...prev, email: backendError }));
        }
      }
    }
  };

  const handleGoogleSignup = () => {
    setError(''); // Clear any previous errors
    setGoogleLoading(true);
    userInitiatedGoogleAction.current = true; // Mark that user initiated action

    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      setError('Google Sign-In is not configured. Please contact support.');
      setGoogleLoading(false);
      userInitiatedGoogleAction.current = false;
      return;
    }

    if (window.google && window.google.accounts && window.google.accounts.id) {
      try {
        // Find the hidden Google button and click it programmatically
        const googleButton = document.getElementById('google-signin-button-hidden');
        if (!googleButton) {
          setError('Google Sign-In is not available. Please refresh the page.');
          setGoogleLoading(false);
          userInitiatedGoogleAction.current = false;
          return;
        }

        // Try to find and click the button, with retries
        const tryClickButton = (attempts = 0) => {
          const buttonElement = googleButton.querySelector('div[role="button"]');
          if (buttonElement) {
            buttonElement.click();
            // Don't set loading to false here - it will be set in the callback
          } else if (attempts < 10) {
            // Retry up to 10 times (1 second total)
            setTimeout(() => tryClickButton(attempts + 1), 100);
          } else {
            // If button still not found, try to render it now
            if (window.google.accounts.id && googleButtonRef.current) {
              try {
                window.google.accounts.id.renderButton(googleButtonRef.current, {
                  theme: 'outline',
                  size: 'large',
                  text: 'signup_with',
                  width: 300,
                });
                setGoogleInitialized(true);
                // Wait a bit for rendering, then try clicking
                setTimeout(() => {
                  const btn = googleButton.querySelector('div[role="button"]');
                  if (btn) {
                    btn.click();
                  } else {
                    setError('Google Sign-In button is not ready. Please refresh the page.');
                    setGoogleLoading(false);
                    userInitiatedGoogleAction.current = false;
                  }
                }, 200);
              } catch (error) {
                console.error('Error rendering Google button:', error);
                setError('Failed to initialize Google Sign-In. Please try again.');
                setGoogleLoading(false);
                userInitiatedGoogleAction.current = false;
              }
            } else {
              setError('Google Sign-In button is not ready. Please refresh the page.');
              setGoogleLoading(false);
              userInitiatedGoogleAction.current = false;
            }
          }
        };

        tryClickButton();
      } catch (error) {
        console.error('Error triggering Google Sign-In:', error);
        setError('Failed to initialize Google Sign-In. Please try again.');
        setGoogleLoading(false);
        userInitiatedGoogleAction.current = false;
      }
    } else {
      setError('Google Sign-In is not available. Please refresh the page and make sure Google Client ID is configured.');
      setGoogleLoading(false);
      userInitiatedGoogleAction.current = false;
    }
  };

  const handleMicrosoftSignup = () => {
    console.log('Microsoft signup clicked');
  };

  const handleLinkedInSignup = () => {
    console.log('LinkedIn signup clicked');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1, y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      className="signup-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <SEO title="Sign Up" path="/signup" description="Create your LawFusion account to access AI-powered legal analysis with multiple AI models." noIndex={true} />
      <motion.div
        className="signup-container"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.div
          className="signup-card"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
          whileHover={{ boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)" }}
        >
          {/* Logo + Title */}
          <motion.div
            className="logo-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Link to="/" style={{ display: 'inline-block', cursor: 'pointer' }}>
              <motion.img
                src="/logo.png"
                alt="LawFusion.AI Logo"
                className="login-logo"
                variants={itemVariants}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </Link>
            <motion.h1
              className="signup-title"
              variants={itemVariants}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            >
              Create your LawFusion Account
            </motion.h1>
            <motion.p
              className="signup-tagline"
              variants={itemVariants}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              Multiple AIs. Stronger Arguments.
            </motion.p>
          </motion.div>

          {/* Hidden Google Button Container */}
          <div
            id="google-signin-button-hidden"
            ref={googleButtonRef}
            style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
          />

          {/* Social Login Buttons */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.button
              type="button"
              className="social-button"
              onClick={() => {
                if (!agreeToGoogleTerms) {
                  setGoogleTermsHint(true);
                  return;
                }
                setGoogleTermsHint(false);
                handleGoogleSignup();
              }}
              disabled={googleLoading}
              variants={itemVariants}
              whileHover={{
                scale: 1.02,
                y: -2,
                boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)"
              }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <motion.svg
                className="social-icon"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.037-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853" />
                <path d="M3.963 10.707c-.18-.54-.282-1.117-.282-1.707 0-.59.102-1.167.282-1.707V4.951H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.049l3.006-2.342z" fill="#FBBC05" />
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.951L3.963 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
              </motion.svg>
              {googleLoading ? 'Signing in...' : 'Continue With Google'}
            </motion.button>
            <div style={{ marginTop: '0.6rem', display: 'flex', justifyContent: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                <input
                  type="checkbox"
                  id="google-terms"
                  checked={agreeToGoogleTerms}
                  onChange={(e) => { setAgreeToGoogleTerms(e.target.checked); if (e.target.checked) setGoogleTermsHint(false); }}
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms-of-service" className="terms-link">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="terms-link">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            </div>
            {googleTermsHint && !agreeToGoogleTerms && (
              <p style={{ color: '#e53e3e', fontSize: '0.8rem', margin: '0.4rem 0 0', textAlign: 'center' }}>
                Please agree to the Terms & Privacy Policy.
              </p>
            )}
          </motion.div>

          {/* Separator */}
          <motion.div
            className="separator"
            variants={itemVariants}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
          >
            <motion.span
              className="separator-line"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ delay: 0.7, duration: 0.5 }}
            />
            <motion.span
              className="separator-text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              or sign up with email
            </motion.span>
            <motion.span
              className="separator-line"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ delay: 0.7, duration: 0.5 }}
            />
          </motion.div>

          {/* Signup Form */}
          <motion.form
            onSubmit={handleSubmit}
            className="signup-form"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="form-group"
              variants={itemVariants}
            >
              <motion.label
                htmlFor="email"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 }}
              >
                Email
              </motion.label>
              <motion.input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={() => handleBlur('email')}
                required
                disabled={emailVerified}
                pattern="[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                placeholder="you@lawfirm.com"
                title="Please enter a valid email address (e.g., user@example.com)"
                className={touched.email && fieldErrors.email ? 'input-error' : (emailVerified ? 'input-disabled' : '')}
                style={emailVerified ? { cursor: 'not-allowed', backgroundColor: '#f5f5f5', color: '#666' } : {}}
                whileFocus={!emailVerified ? {
                  scale: 1.02,
                  boxShadow: "0 0 0 3px rgba(66, 133, 244, 0.1)",
                  borderColor: touched.email && fieldErrors.email ? "#dc3545" : "#4285f4"
                } : {}}
                transition={{ duration: 0.2 }}
              />
              {touched.email && fieldErrors.email && (
                <motion.div
                  className="field-error"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {fieldErrors.email}
                </motion.div>
              )}
            </motion.div>

            <motion.div
              className="form-group password-group"
              variants={itemVariants}
            >
              <motion.label
                htmlFor="password"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 }}
              >
                Password
              </motion.label>
              <div className="password-input-wrapper">
                <motion.input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => handleBlur('password')}
                  required
                  placeholder="Enter your password"
                  minLength="8"
                  title="Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
                  className={touched.password && fieldErrors.password ? 'input-error' : ''}
                  whileFocus={{
                    scale: 1.02,
                    boxShadow: "0 0 0 3px rgba(66, 133, 244, 0.1)",
                    borderColor: touched.password && fieldErrors.password ? "#dc3545" : "#4285f4"
                  }}
                  transition={{ duration: 0.2 }}
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.password && fieldErrors.password && (
                <motion.div
                  className="field-error"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {fieldErrors.password}
                </motion.div>
              )}
              {/* Password Requirements Helper */}
              {!touched.password || !fieldErrors.password ? (
                <p style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  marginTop: '0.5rem',
                  marginBottom: 0,
                  lineHeight: '1.4'
                }}>
                  Password must contain: 8+ characters, uppercase, lowercase, number, and special character
                </p>
              ) : null}
            </motion.div>

            <motion.div
              className="form-group"
              variants={itemVariants}
            >
              <motion.label
                htmlFor="firmName"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1 }}
              >
                Firm, Company, or Your Name
              </motion.label>
              <motion.input
                type="text"
                id="firmName"
                value={firmName}
                onChange={handleFirmNameChange}
                onBlur={() => handleBlur('firmName')}
                required
                pattern="[a-zA-Z0-9\s'.-]+"
                placeholder="Firm, company or full name"
                title="Firm name can only contain letters, numbers, spaces, hyphens, apostrophes, and periods"
                className={touched.firmName && fieldErrors.firmName ? 'input-error' : ''}
                whileFocus={{
                  scale: 1.02,
                  boxShadow: "0 0 0 3px rgba(66, 133, 244, 0.1)",
                  borderColor: touched.firmName && fieldErrors.firmName ? "#dc3545" : "#4285f4"
                }}
                transition={{ duration: 0.2 }}
              />
              {touched.firmName && fieldErrors.firmName && (
                <motion.div
                  className="field-error"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {fieldErrors.firmName}
                </motion.div>
              )}
            </motion.div>

            <motion.div
              className="form-group"
              variants={itemVariants}
            >
              <motion.label
                htmlFor="role"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 }}
              >
                Role
              </motion.label>
              <div className="select-wrapper">
                <motion.select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="role-select"
                  whileFocus={{
                    scale: 1.02,
                    boxShadow: "0 0 0 3px rgba(66, 133, 244, 0.1)",
                    borderColor: "#4285f4"
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <option value="Attorney">Attorney</option>
                  <option value="Paralegal">Paralegal</option>
                  <option value="Legal Researcher">Legal Researcher</option>
                  <option value="Other">Other</option>
                </motion.select>
                <motion.span
                  className="select-arrow"
                  animate={{ y: [0, 3, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  ▼
                </motion.span>
              </div>
            </motion.div>

            {/* Terms Checkbox */}
            <motion.div
              className="terms-checkbox"
              variants={itemVariants}
            >
              <motion.input
                type="checkbox"
                id="terms"
                checked={agreeToEmailTerms}
                onChange={(e) => {
                  setAgreeToEmailTerms(e.target.checked);
                  if (e.target.checked) setEmailTermsHint(false);
                }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
              <label htmlFor="terms">
                I agree to the{' '}
                <Link to="/terms-of-service" className="terms-link" onClick={(e) => e.stopPropagation()}>
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link to="/privacy-policy" className="terms-link" onClick={(e) => e.stopPropagation()}>
                  Privacy Policy
                </Link>
                .
              </label>
            </motion.div>
            {emailTermsHint && !agreeToEmailTerms && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ color: '#e53e3e', fontSize: '0.8rem', margin: '-0.5rem 0 1rem 2.2rem' }}
              >
                Please agree to the Terms & Privacy Policy to continue.
              </motion.p>
            )}

            <AnimatePresence>
              {verificationEmailSent && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  style={{
                    padding: '1.25rem',
                    borderRadius: '10px',
                    marginBottom: '1rem',
                    textAlign: 'center',
                    border: '1px solid rgba(34,197,94,0.25)',
                    background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(22,163,74,0.04))',
                  }}
                >
                  <div style={{
                    width: '48px', height: '48px', margin: '0 auto 0.75rem',
                    borderRadius: '50%', background: 'rgba(34,197,94,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="M22 4L12 13L2 4" />
                    </svg>
                  </div>
                  <p style={{ margin: '0 0 0.4rem', fontWeight: 700, fontSize: '1rem', color: '#16a34a' }}>
                    Verification Email Sent
                  </p>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    We sent a verification link to <strong style={{ color: '#1A3DBE' }}>{email}</strong>
                  </p>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Click the link in the email to complete your signup. Check spam if you don't see it.
                  </p>
                </motion.div>
              )}
              {error && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, y: -10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Create Account Button */}
            <motion.button
              type="submit"
              className="create-account-button"
              disabled={loading || verificationEmailSent}
              variants={itemVariants}
              whileHover={!loading && !verificationEmailSent ? {
                scale: 1.02,
                boxShadow: "0 8px 20px rgba(66, 133, 244, 0.4)",
                y: -2
              } : {}}
              whileTap={!loading && !verificationEmailSent ? { scale: 0.98 } : {}}
              transition={{ type: "spring", stiffness: 400 }}
            >
              {loading ? (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ repeat: Infinity, duration: 1 }}
                >
                  {emailVerified ? 'Creating Account...' : 'Sending Verification Email...'}
                </motion.span>
              ) : verificationEmailSent ? (
                'Verification Email Sent'
              ) : emailVerified ? (
                'Complete Signup'
              ) : (
                'Send Verification Email'
              )}
            </motion.button>
          </motion.form>

          <Footer variant="links-only" />

          {/* Sign In Link */}
          <motion.div
            className="signin-link"
            variants={itemVariants}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
          >
            Already have an account?{' '}
            <motion.span
              style={{ display: "inline-block" }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/login">Sign in</Link>
            </motion.span>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Signup;

